// This class knows:
//   - Nothing about c9-tracking
//   - All about claim-form-app
//
// Export short meaningful tracking functions whose name
// actually make sense in the context they are used
//
// Do not expose c9-tracking inner-workings, like interact etc
//
import {
    trackInfoEvent,
    trackInfoEventValue,
    trackConversionItem,
    trackFault,
    trackNavigate,
    trackInteractEvent,
    trackInteractEventValue
} from './trackingWrapper';
import { getRawUrlWithoutParameters } from './getUrlRaw';
import { FormConfig } from '@topdanmark-onlinesolutions/claim-form-types';
import { AppStateValues } from '../state/appState';

export const trackFormInit = (formConfig: FormConfig, pageId: string) => {
    trackInfoEvent(formConfig, 'form-init', pageId);
};

export const trackABHighwaySplitTest = (formConfig: FormConfig, pageId: string) => {
    const abHighwaySession = sessionStorage.getItem('ab-highway-test');
    let event = 'the-user-came-from-direct-url';
    if (abHighwaySession === '#oh') {
        event = 'the-user-came-from-the-old-highway';
    } else if (abHighwaySession === '#nh') {
        event = 'the-user-came-from-the-new-highway';
    }
    trackInfoEvent(formConfig, event, pageId);
};
export const trackABHighwaySplitTestSubmission = (formConfig: FormConfig) => {
    const abHighwaySession = sessionStorage.getItem('ab-highway-test');
    const formId = formConfig.formId;
    let event = 'the-user-submitted-the-form-using-direct-url';
    if (abHighwaySession === '#oh') {
        event = 'the-user-submitted-the-form-using-old-highway';
    } else if (abHighwaySession === '#nh') {
        event = 'the-user-submitted-the-form-using-new-highway';
    }
    trackInfoEvent(formConfig, event, formId);
};
export const trackABHighwaySplitTestSummary = (formConfig: FormConfig) => {
    const abHighwaySession = sessionStorage.getItem('ab-highway-test');
    let event = 'summary-page-shown-direct-url';
    if (abHighwaySession === '#oh') {
        event = 'summary-page-shown-old-highway';
    } else if (abHighwaySession === '#nh') {
        event = 'summary-page-shown-new-highway';
    }
    trackInfoEvent(formConfig, event, '');
};

export const trackDidTheUserGoThroughTheHighway = (formConfig: FormConfig, pageId: string) => {
    const event = !!sessionStorage.getItem('ClaimForm2020|claimHighway')
        ? 'the-user-came-from-the-highway'
        : 'the-user-did-not-come-from-the-highway';

    trackInfoEvent(formConfig, event, pageId);
};

export const trackSubmitWithFormFromSearchResultInHighway = (formConfig: FormConfig) => {
    const highwayStorage = sessionStorage.getItem('claimHighway');
    console.log(highwayStorage);
    if (highwayStorage) {
        const parsedHighwayStorage = JSON.parse(highwayStorage);
        const isFromSearchResult = parsedHighwayStorage.selectionResult.find(
            (selection: { fromSearchResult: boolean }) => selection.fromSearchResult
        );
        if (isFromSearchResult) {
            console.log('Tracking: User submitted form from search result in highway');
            trackInfoEvent(formConfig, 'the-user-submitted-the-form-from-search-result-in-highway', '');
        }
    }
};

export const trackLeaveBeforeSubmission = (formConfig: FormConfig, pageId: string) => {
    trackInfoEvent(formConfig, 'leaving-before-submission', pageId);
};

export const trackSubmissionSuccess = (formConfig: FormConfig) => {
    trackConversionItem(formConfig);
};

export const trackTimeSpentOnFillingTheForm = (formConfig: FormConfig, formStartTime: number | undefined) => {
    if (formStartTime === undefined) {
        throw new Error('formStartTime cannot be empty');
    }

    const formEndTime = Date.now();

    const durationInSeconds = Math.round((formEndTime - formStartTime) / 1000);

    trackInfoEventValue(formConfig, 'form-duration-seconds', durationInSeconds, '');
};

export const trackTimeSpentOnPage = (formConfig: FormConfig, pageId: string, timeSpentOnPageInSeconds: number) => {
    trackInteractEventValue(formConfig, 'page-time-spent', timeSpentOnPageInSeconds, pageId);
};

export const trackSubmissionFail = (formConfig: FormConfig, error: any) => {
    trackFault(formConfig, error, '');
};

export const trackSummaryPageShown = (formConfig: FormConfig) => {
    trackInfoEvent(formConfig, 'summary-page-shown', '');
};

export const trackUserClickedEditOnResumePage = (formConfig: FormConfig, elementId: string) => {
    trackInteractEventValue(formConfig, 'the-user-clicked-edit-on-a-question-on-the-resume-page', elementId, '');
};

export const trackUserClickedBack = (formConfig: FormConfig, pageId: string) => {
    trackInteractEvent(formConfig, 'the-user-clicked-back', pageId);
};

export const trackUserClickedNavigateToResumePage = (formConfig: FormConfig, pageId: string) => {
    trackInteractEvent(formConfig, 'the-user-clicked-navigate-to-resume-page', pageId);
};

export const trackNavigationToReceiptPage = (formConfig: FormConfig) => {
    // @TODO
    //   - Should it actually be the URL without query parms?
    //     What about all the 'ref=' that tells where the user came from?
    const url = getRawUrlWithoutParameters();

    trackNavigate(formConfig, url, 'receiptPage');
};

export const trackContinueWithSavedFormData = (formConfig: FormConfig, source: string) => {
    trackInfoEventValue(formConfig, 'resume-from-save', source, '');
};

export const trackDeleteSavedFormData = (formConfig: FormConfig) => {
    trackInfoEvent(formConfig, 'delete-from-save', '');
};

export const trackSubmitAfterResumeFromSave = (
    formConfig: FormConfig,
    resumeFromSaved: AppStateValues['resumeFromSave']
) => {
    if (!resumeFromSaved.isResumed) {
        return;
    }

    trackInteractEvent(formConfig, 'submit-after-resume-from-save', '');

    const todayTime = new Date().getTime();
    const firstSavedDate = resumeFromSaved.firstSavedDate * 1000; // timestamp is stored in seconds

    const diffTime = todayTime - firstSavedDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    trackInteractEventValue(formConfig, 'submit-days-after-resume-from-save', diffDays, '');
};
